import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Alert,
  Grid,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import { School, MusicNote, Brush, TrendingUp, ArrowForward, Search, LocationOn, ArrowBack } from '@mui/icons-material';
import { useGuest } from '../context/GuestContext';
import {
  getGuestCourseTypes,
  getGuestCoursesByType,
  registerGuestCourse,
} from '../services/guestApi';
import type { GuestCourseBrowseDTO, GuestCourseTypeDTO } from '../services/guestApi';
import { trackGuestEvent } from '../services/guestAnalyticsService';

const iconByType: Record<string, React.ReactNode> = {
  SECONDARY_SCHOOL: <School sx={{ fontSize: 40, color: 'primary.main' }} />,
  PRIMARY_SCHOOL: <School sx={{ fontSize: 40, color: 'primary.main' }} />,
  PIANO: <MusicNote sx={{ fontSize: 40, color: 'primary.main' }} />,
  CHESS: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
  MUSIC: <MusicNote sx={{ fontSize: 40, color: 'primary.main' }} />,
  ART: <Brush sx={{ fontSize: 40, color: 'primary.main' }} />,
};

const courseTypeLabel = (courseType: string, displayName?: string) =>
  displayName ?? courseType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const GuestAvailableCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { guestId, ensureSession, error: guestError } = useGuest();
  const [courseTypes, setCourseTypes] = useState<GuestCourseTypeDTO[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [coursesByType, setCoursesByType] = useState<Record<string, GuestCourseBrowseDTO[]>>({});
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [registering, setRegistering] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeTypes = useMemo(
    () => courseTypes.filter((ct) => ct.status === 'ACTIVE'),
    [courseTypes]
  );

  const secondaryFirstTypes = useMemo(() => {
    const secondary = activeTypes.filter((t) => t.courseType === 'SECONDARY_SCHOOL');
    const rest = activeTypes.filter((t) => t.courseType !== 'SECONDARY_SCHOOL');
    return [...secondary, ...rest];
  }, [activeTypes]);

  const selectedType = secondaryFirstTypes[tabIndex]?.courseType;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [gid, types] = await Promise.all([ensureSession(), getGuestCourseTypes()]);
        if (!cancelled) {
          setCourseTypes(types);
          trackGuestEvent(gid, { eventType: 'catalog_hub_viewed' });
        }
      } catch {
        if (!cancelled) setError('Could not load course types.');
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureSession]);

  const loadCoursesForTab = useCallback(
    async (courseType: string) => {
      if (coursesByType[courseType]) return;
      setLoadingCourses(true);
      try {
        const courses = await getGuestCoursesByType(courseType);
        setCoursesByType((prev) => ({ ...prev, [courseType]: courses }));
      } catch {
        setError(`Could not load courses for ${courseTypeLabel(courseType)}.`);
      } finally {
        setLoadingCourses(false);
      }
    },
    [coursesByType]
  );

  useEffect(() => {
    if (!selectedType) return;
    loadCoursesForTab(selectedType);
  }, [selectedType, loadCoursesForTab]);

  const handleTryCourse = async (courseId: number) => {
    const gid = guestId ?? (typeof window !== 'undefined' ? localStorage.getItem('guestId') : null);
    if (!gid) {
      setError('Guest session not ready. Please refresh.');
      return;
    }
    setRegistering(courseId);
    setError(null);
    try {
      trackGuestEvent(gid, { eventType: 'path_selected', entryMethod: 'QUICK_COURSE' });
      const res = await registerGuestCourse(gid, { courseId, entryMethod: 'QUICK_COURSE' });
      navigate(`/guest/course/${res.courseId}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err.response?.data?.message ?? (e instanceof Error ? e.message : 'Registration failed');
      setError(msg);
    } finally {
      setRegistering(null);
    }
  };

  if (loadingTypes) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const tabCourses = selectedType ? coursesByType[selectedType] ?? [] : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/guest')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper elevation={0} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Try available courses
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Search topics, find courses by school, or pick a course below. You can access up to two topics as a guest.
        </Typography>

        {guestError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {guestError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          How would you like to try?
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: 1,
                borderColor: 'primary.main',
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
              }}
              onClick={() => {
                const gid = guestId ?? localStorage.getItem('guestId');
                trackGuestEvent(gid, { eventType: 'path_selected', entryMethod: 'SEARCH' });
                navigate('/guest/search');
              }}
            >
              <CardContent sx={{ flex: 1, textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Search sx={{ fontSize: 56, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Search and try a topic
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Search across subjects and try a topic. View content, quiz and resources.
                </Typography>
                <Button variant="contained" endIcon={<ArrowForward />}>
                  Search topics
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: 1,
                borderColor: 'secondary.main',
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
              }}
              onClick={() => {
                const gid = guestId ?? localStorage.getItem('guestId');
                trackGuestEvent(gid, { eventType: 'path_selected', entryMethod: 'BY_LOCATION' });
                navigate('/guest/by-location');
              }}
            >
              <CardContent sx={{ flex: 1, textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <LocationOn sx={{ fontSize: 56, color: 'secondary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Browse courses by my school
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select your country, council and school to see courses for your curriculum.
                </Typography>
                <Button variant="outlined" color="secondary" endIcon={<ArrowForward />}>
                  Browse by school
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Quick try: choose a course
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a course type, then select a course. Guest access is limited to two topics per visit.
        </Typography>

        {secondaryFirstTypes.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No course types available at the moment. Please try again later or sign up for updates.
          </Alert>
        )}

        {secondaryFirstTypes.length > 0 && (
          <>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              {secondaryFirstTypes.map((ct) => (
                <Tab key={ct.courseType} label={ct.displayName || courseTypeLabel(ct.courseType)} />
              ))}
            </Tabs>

            {loadingCourses && !coursesByType[selectedType ?? ''] ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : tabCourses.length === 0 ? (
              <Alert severity="info">
                No active courses for {courseTypeLabel(selectedType ?? '', secondaryFirstTypes[tabIndex]?.displayName)} yet.
                Try browse by school or search.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {tabCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} key={course.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                          {iconByType[course.courseType] ?? <School sx={{ fontSize: 40 }} />}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap title={course.name}>
                              {course.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {courseTypeLabel(course.courseType)}
                            </Typography>
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, gap: 0.5 }}>
                          {course.countryLabel && (
                            <Chip label={course.countryLabel} size="small" variant="outlined" color="primary" />
                          )}
                          {course.board && (
                            <Chip label={course.board} size="small" variant="outlined" />
                          )}
                          <Chip
                            label={`${course.topicCount} topic${course.topicCount === 1 ? '' : 's'}`}
                            size="small"
                            color={course.topicCount > 0 ? 'success' : 'default'}
                          />
                        </Stack>
                        {course.curriculumAlignment && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {course.curriculumAlignment}
                          </Typography>
                        )}
                        {course.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              flex: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {course.description}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={registering === course.id || course.topicCount === 0}
                          onClick={() => handleTryCourse(course.id)}
                          endIcon={registering === course.id ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                          sx={{ mt: 'auto' }}
                        >
                          {registering === course.id
                            ? 'Starting…'
                            : course.topicCount === 0
                              ? 'No topics yet'
                              : 'Try this course'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default GuestAvailableCoursesPage;
